import {
    Box,
    Button,
    HStack,
    useToast,
} from "@chakra-ui/react";
import { MdBusiness, MdPayment, MdLink } from "react-icons/md";
import { useState } from "react";
import AnimatedSaveButton from "../../../components/animations/AnimatedSaveButton";
import { useUnsavedChanges } from "../../../hooks/useUnsavedChanges";
import FormField from "../../../components/fields/FormField";
import FormSection from "../../../components/fields/FormSection";
import PageCard from "../../../components/page-card";
import { getAppConfig } from "../../../config";
import { translate } from "../../../i18n";
import { useCreateStripeAccountLink } from "../hooks/useCreateStripeAccountLink";
import { useElevatedAdminAccess } from "../hooks/useIsElevatedAdmin";
import { useSelectedOrganization } from "../hooks/useSelectedOrganization";
import { useUpdateOrganizationBaseUrl } from "../hooks/useUpdateOrganizationBaseUrl";

export default function OrganizationDetails() {
    const { selectedOrganization } = useSelectedOrganization();
    const { mutateAsync, isLoading } = useCreateStripeAccountLink();
    const { isElevated: isElevatedAdmin } = useElevatedAdminAccess();
    const toast = useToast();

    const [baseUrlValue, setBaseUrlValue] = useState(selectedOrganization?.baseUrl || '');
    const [isEditingBaseUrl, setIsEditingBaseUrl] = useState(false);
    useUnsavedChanges(isEditingBaseUrl && baseUrlValue !== (selectedOrganization?.baseUrl || ''));

    const { mutateAsync: updateBaseUrl, isLoading: isUpdatingBaseUrl } =
        useUpdateOrganizationBaseUrl(selectedOrganization?.id || '', () => {
            setIsEditingBaseUrl(false);
            toast({ title: 'Base URL updated', status: 'success', duration: 2000 });
        });

    const handleSaveBaseUrl = async () => {
        try {
            await updateBaseUrl({ baseUrl: baseUrlValue });
        } catch {
            toast({ title: translate('common.error'), status: 'error', duration: 3000 });
        }
    };

    return (
        <PageCard
                title={translate('tabs.organization')}
                rightHeader={
                    <Button
                        colorScheme="brand"
                        isLoading={isLoading}
                        onClick={() => {
                            mutateAsync({
                                organizationId: selectedOrganization?.id,
                                refreshUrl: `${getAppConfig().dashboardUrl}`,
                                returnUrl: `${getAppConfig().dashboardUrl}`,
                            }).then((result) => {
                                window.location.href = result.url;
                            }).catch(() => {
                                toast({ title: translate('common.error'), status: 'error', duration: 3000 });
                            });
                        }}
                    >
                        {translate('organization.stripe_setup')}
                    </Button>
                }
            >
                <Box display="flex" flexDirection="column" gap="28px" px={6} pb={6}>
                    <FormSection label={translate('tabs.organization')} icon={MdBusiness} columns={2}>
                        <FormField
                            label={translate('organization.name')}
                            value={selectedOrganization?.name || ''}
                            isDisabled
                            onChange={() => {}}
                        />
                        <FormField
                            label={translate('organization.organization_id')}
                            value={selectedOrganization?.id || ''}
                            isDisabled
                            onChange={() => {}}
                            helperText="Unique identifier for API integrations"
                        />
                    </FormSection>

                    <FormSection label={translate('organization.stripe_setup')} icon={MdPayment} columns={1}>
                        <FormField
                            label={translate('organization.stripe_account_id')}
                            value={selectedOrganization?.stripeAccountId || ''}
                            isDisabled
                            onChange={() => {}}
                            helperText="Connected Stripe account for payment processing"
                        />
                    </FormSection>

                    {(isElevatedAdmin || selectedOrganization?.baseUrl) && (
                        <FormSection label="Configuration" icon={MdLink} columns={1}>
                            {isElevatedAdmin ? (
                                <Box>
                                    <FormField
                                        label="Base URL"
                                        value={isEditingBaseUrl ? baseUrlValue : (selectedOrganization?.baseUrl || '')}
                                        isDisabled={!isEditingBaseUrl}
                                        onChange={(value: string) => setBaseUrlValue(value)}
                                        helperText="Custom domain for the organization's mobile app"
                                    />
                                    <HStack mt={2} spacing={2}>
                                        {isEditingBaseUrl ? (
                                            <>
                                                <AnimatedSaveButton
                                                    size="sm"
                                                    label={translate('common.save')}
                                                    onClick={handleSaveBaseUrl}
                                                />
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => {
                                                        setIsEditingBaseUrl(false);
                                                        setBaseUrlValue(selectedOrganization?.baseUrl || '');
                                                    }}
                                                >
                                                    {translate('common.cancel')}
                                                </Button>
                                            </>
                                        ) : (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                    setBaseUrlValue(selectedOrganization?.baseUrl || '');
                                                    setIsEditingBaseUrl(true);
                                                }}
                                            >
                                                {translate('common.edit')}
                                            </Button>
                                        )}
                                    </HStack>
                                </Box>
                            ) : (
                                <FormField
                                    label="Base URL"
                                    value={selectedOrganization?.baseUrl || ''}
                                    isDisabled
                                    onChange={() => {}}
                                />
                            )}
                        </FormSection>
                    )}
                </Box>
        </PageCard>
    );
}
